import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, orderBy, limit, runTransaction, serverTimestamp } from "firebase/firestore";
import { getOrInitStock, calculateClosing } from "../utils/stockUtils";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";

function DistributorInventory() {
  const [userProfile, setUserProfile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skuMaster, setSkuMaster] = useState({});
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate("/"); return; }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        setUserProfile(profile);
        loadInventory(profile, date);
      }
    });
    return () => unsub();
  }, [navigate, date]);

  const loadInventory = async (profile, targetDate) => {
    setLoading(true);
    try {
      // 1. Load SKU Master
      const skuSnap = await getDocs(collection(db, "skuMaster"));
      const smap = {};
      const slist = [];
      skuSnap.forEach(d => {
        const data = d.data();
        const ident = String(data.sku || data.Name || "").trim().toUpperCase();
        smap[ident] = data;
        slist.push(ident);
      });
      setSkuMaster(smap);

      // 2. Get/Initialize Distributor Stock Daily Doc using CENTRALIZED UTILITY
      const distId = profile.partyId || "UNKNOWN";
      const stockData = await getOrInitStock("distributor", distId, targetDate, profile.clientId);
      const data = stockData.inventory || {};

      // 3. Construct Rows
      const finalRows = slist.map(sku => {
          const skuUpper = sku.trim().toUpperCase();
          const info = data[skuUpper] || { opening: 0 };
          
          const opening = Number(info.opening || 0);
          const received = Number(info.received || 0) + Number(info.purchase || 0);
          const sold = Number(info.sold || 0) + Number(info.sale || 0);
          const closing = opening + received - sold;

          return {
              sku: skuUpper,
              name: smap[skuUpper]?.Name || skuUpper,
              opening,
              received,
              sold,
              closing
          };
      });

      setRows(finalRows);
    } catch (err) {
      console.error("Error loading distributor inventory:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Layout><div style={{ padding: 20 }}>Loading Inventory...</div></Layout>;

  return (
    <Layout>
      <div className="layout-content">
        {/* HEADER SECTION */}
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
                <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Inventory Manifest</h1>
                <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Real-time stock ledger for <strong>{userProfile?.partyId}</strong>. Opening, Inward, and Closing status.</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, background: "var(--surface)", padding: "8px 16px", borderRadius: 12, border: "1px solid var(--border)" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>AUDIT DATE:</span>
                <input 
                    type="date" 
                    value={date} 
                    onChange={e => setDate(e.target.value)}
                    style={{ border: "none", background: "transparent", fontSize: 16, fontWeight: 800, color: "var(--primary)", outline: "none", cursor: "pointer" }}
                />
            </div>
        </div>

        <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-hover)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "var(--text-main)" }}>STOCK DISCLOSURE MATRIX</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>{rows.length} ACTIVE SKUs</span>
            </div>
            
            <div style={{ maxHeight: 700, overflowY: "auto" }}>
                <table className="enterprise-table">
                    <thead>
                        <tr>
                            <th style={{ paddingLeft: 24 }}>Portfolio Item (SKU)</th>
                            <th style={{ textAlign: "right" }}>Opening Pulse</th>
                            <th style={{ textAlign: "right" }}>Inward Inflow</th>
                            <th style={{ textAlign: "right" }}>Sales Outflow</th>
                            <th style={{ textAlign: "right", paddingRight: 24 }}>Current Holding</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>
                                    No stock movements detected for the selected audit cycle.
                                </td>
                            </tr>
                        ) : (
                            rows.map(r => (
                                <tr key={r.sku}>
                                    <td style={{ paddingLeft: 24 }}>
                                        <div style={{ fontWeight: 800, color: "var(--primary)", fontSize: 15 }}>{r.sku}</div>
                                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{r.name}</div>
                                    </td>
                                    <td style={{ textAlign: "right", fontWeight: 700 }}>{r.opening.toLocaleString()}</td>
                                    <td style={{ textAlign: "right", color: "var(--success)", fontWeight: 800 }}>
                                        {r.received > 0 ? `+ ${r.received.toLocaleString()}` : "—"}
                                    </td>
                                    <td style={{ textAlign: "right", color: "var(--error)", fontWeight: 800 }}>
                                        {r.sold > 0 ? `- ${r.sold.toLocaleString()}` : "—"}
                                    </td>
                                    <td style={{ textAlign: "right", paddingRight: 24 }}>
                                        <span style={{ 
                                          fontSize: 18, 
                                          fontWeight: 900, 
                                          color: r.closing > 0 ? "var(--text-main)" : "var(--error)",
                                          background: "var(--surface-hover)",
                                          padding: "4px 12px",
                                          borderRadius: 8
                                        }}>
                                          {r.closing.toLocaleString()}
                                        </span>
                                    </td>
                                </tr>
                            )))}
                    </tbody>
                </table>
            </div>
        </div>

        <div style={{ marginTop: 32, padding: 24, borderRadius: 16, background: "rgba(255, 107, 0, 0.05)", border: "1px dashed var(--primary)" }}>
            <h4 style={{ margin: "0 0 12px 0", fontSize: 14, fontWeight: 800 }}>Carry-Forward Protocol</h4>
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
                Opening stock for any date is automatically calculated from the previous day's closing pulse. Ensure all DS billings are completed to reflect accurate "Sales Outflow" values.
            </p>
        </div>
      </div>
    </Layout>
  );
}

export default DistributorInventory;
